import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef,
} from "@/styles/STableLayout";
import { CommonListParams } from "@/types/common";
import { Supplier } from "@/types/purchase";
import { App, Button, Flex, Input, Tooltip } from "antd";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useDataList from "@/hooks/useDataList";
import apis from "@/apis/supplier";
import useDataAdd from "@/hooks/useDataAdd";
import useDataDel from "@/hooks/useDataDel";
import { AnyObject } from "antd/es/_util/type";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import { ColumnsType } from "antd/lib/table";
import { User, UserAdd } from "@/types/user";
import useUserColumn from "../../hooks/useUserColumn";
import UserForm from "./UserForm";
import { randomStr8 } from "@/utils/randomStr8";
import { userUpdatePassword } from "@/apis/user";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

interface PriceProps {
  supplier: Supplier;
}

interface SupplierUserFilters {
  keyword: string;
}

const INITIAL_SUPPLIER_USER_FILTERS: SupplierUserFilters = {
  keyword: "",
};

const SupplierUserList: FC<PriceProps> = ({ supplier }) => {
  const { t } = useTranslation();
  const { notification, modal } = App.useApp();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [userList, total, listLoading, { fetchList, addItem, rmItem }] = useDataList<User, [string, CommonListParams<string>]>({ fetchApi: apis.supplierUsers });
  const { loading: addLoading, add } = useDataAdd<[string, UserAdd, string], User>({ api: apis.supplierUserAdd });
  const { del } = useDataDel<[string, string]>({ api: apis.supplierUserDel });

  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<SupplierUserFilters>(INITIAL_SUPPLIER_USER_FILTERS);
  const { keyword } = draftFilters;
  const [addOpen, setAddOpen] = useState<boolean>(false);

  // ✅ 组件卸载时取消请求
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const { onConfirm } = useFrontDelTip<void, [string, string]>({ cb: (sid, uid) => onDel(sid, uid) });
  const columns = useUserColumn({ onDel: (uid) => onConfirm(supplier.id, uid), onResetPwd: (uid) => resetPwd(uid) });

  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_SUPPLIER_USER_FILTERS);
    fetch(0, nextFilters);
    tableRef.current?.resetTablePage();
    // 切换供应商时重置筛选，查询由事件显式提交。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier.id, replaceFilters]);

  const errors: {
    [key in number]: string;
  } = {
    10010: t("common.networkError"),
    71701: t("purchase.priceExisted"),
  };

  const handleSearch = () => {
    const nextFilters = commitFilters();
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_SUPPLIER_USER_FILTERS);
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const headerChildren = (
    <Flex style={{ width: "100%" }} justify="space-between">
      <Button type="primary" onClick={() => setAddOpen(true)}>
        {t("purchase.supplierUserAdd")}
      </Button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={listLoading}
        >
          {({ submit }) => (
            <Tooltip title={t("purchase.supplierUserSearch")}>
              <Input
                value={keyword}
                onChange={event =>
                  updateDraftFilters({ keyword: event.target.value.trim() })
                }
                placeholder={t("purchase.supplierUserSearch")}
                onPressEnter={submit}
                style={{ width: 200 }}
              />
            </Tooltip>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  const fetch = (offset: number, filters: SupplierUserFilters) => {
    // ✅ 取消上一次请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // ✅ 创建新的 AbortController
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE
    fetchList(supplier.id, {
      body: filters.keyword,
      pagination: { offset, take },
      controller, // ✅ 传递 controller 用于取消请求
    }).catch((err) => {
      // ✅ 忽略取消的请求
      if (err?.name === 'AbortError') {
        return;
      }
    });
  };

  const onDel: (sid: string, uid: string) => Promise<void> = (sid, uid) => {
    return del(sid, uid).then(() => {
      rmItem(uid);
      notification.success({ placement: 'top', message: t("common.rmSuccess") });
    });
  };



  const resetPwd = (uid: string) => {
    let newPwd = randomStr8()
    modal.confirm({
      title: t("purchase.supplierReset"),
      content: t("purchase.resetPwd", { newPwd }),
      onOk: () => updatePwd({ id: uid, password: newPwd })
    })
  }

  const updatePwd = (data: { id: string, password: string }) => {

    userUpdatePassword(data.id || '', data.password)
      .then(() => {
        notification.success({ placement: 'top', message: t('member.pwdChanged') });
      })
      .catch((err) => {
        notification.error({ placement: 'top', message: t("purchase.editPwdError"), description: err })
      })
  }

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        loading={listLoading}
        dataSource={userList}
        columns={columns as ColumnsType<AnyObject>}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={offset => fetch(offset, appliedFilters)}
      />
      <UserForm
        supplierCode={supplier.code}
        key={"addPrice"}
        open={addOpen}
        confirmLoading={addLoading}
        handleCancel={() => setAddOpen(false)}
        handleOk={(data) => {
          return new Promise((resolve, reject) => {
            add(supplier.id, { name1: data.name, mobile: data.mobile, areaCode: data.areaCode }, data.password)
              .then((res) => {
                addItem(res);
                setAddOpen(false);
                notification.success({ placement: 'top', message: t('common.addSuccess') })
                resolve();
              })
              .catch((err) => {
                notification.error({ placement: 'top',
                  message: t("common.addError"),
                  description: errors[err] ?? t("common.serverError"),
                });
              });
          });
        }}
        leftTitle={t("common.add")}
      />
    </Flex>
  );
};

export default SupplierUserList;
